// The printable return slip: a letter-size packing list that travels in the box.
//
// ── WHY THIS IS NOT IN printing-utils.js ─────────────────────────────────────
// That module owns LABEL STOCK — 1.5in x 0.375in text and 11.4mm QR squares, one
// set of die-cut geometries shared by every sticker the shop prints. This is an
// 8.5x11 document for a vendor's receiving desk. Putting it there would make
// "printing-utils owns every label format" false, and that sentence is what keeps
// the label geometry in one place.
//
// ── THE ONE THING THIS FILE MUST GET RIGHT ───────────────────────────────────
// The ID column shows `line.label_identity` VERBATIM — the same stored string the
// sticker carries. Nothing here derives, formats, pads or prettifies it. That
// identity is the entire link between a board in the box and a row on this page,
// and any transformation applied on one side and not the other breaks it.
//
// The full UID prints BESIDE the identity, in a secondary column, because a manifest
// line tag is unambiguous but not meaningful: the vendor's own process works off
// the hardware UID, and there is room for 24 characters here where there is not on
// a sticker.
//
// ── THE PURCHASE ORDER IS THE VENDOR'S HANDLE, THE BOX IS OURS ───────────────
// Every row prints the purchase order it arrived on, and the header names the
// orders once, because that is what a vendor's desk files a return under. Our
// receipt box barcode identifies a carton on our shelf and means nothing to the
// people reading this page, so it prints in ONE case only: a stock row with no
// order to print. That row has no hardware UID either, and a row with no handle
// at all cannot be talked about on the phone by either side.
//
// An order placed with a DIFFERENT vendor than this slip is going to is left
// off, row and header both (`po_is_other_vendor`). Nothing stops a board bought
// from one vendor going back on another's manifest, and that number is the first
// vendor's business. The manifest screen says so on the row.
//
// ── THE HEADERS CHANGE WHEN LOOSE STOCK IS ON THE MANIFEST ───────────────────
// A slip of boards keeps the words a board shipment uses: Board and Model. Put a
// count of loose stock, or a sticker-tracked unit (GPS, LED), on the same slip
// and Model stops being true for those rows, so the pair becomes Kind and Item. Conditional rather than renamed
// outright, because the board-only slip is the common one and its paper should
// not change more than it must under a vendor who has been reading it for a year.

import {
  isPartInstanceLine,
  isQuantityLine,
  type ManifestDetail,
  type ManifestLine,
} from './boardReturnsApi';

/** Unescaped user text reaches document.write, so it goes through here first. */
function esc(value: unknown): string {
  return String(value ?? '').replace(/[&<>"']/g, (ch) => ({
    '&': '&amp;',
    '<': '&lt;',
    '>': '&gt;',
    '"': '&quot;',
    "'": '&#39;',
  }[ch] as string));
}

/** Placeholder for an empty cell. The one place the em-dash glyph is allowed. */
const EMPTY = '—';

function cell(value: unknown): string {
  const text = String(value ?? '').trim();
  return text ? esc(text) : EMPTY;
}

/**
 * A value, or a ruled space to write it in by hand.
 *
 * An em-dash is right for a table cell, where it means "there is nothing here and
 * that is the answer". It is wrong for the vendor's RMA number, because that
 * number usually does not exist YET: the vendor issues it over the phone while
 * somebody is holding the box. A dash tells them there is nothing to fill in; a
 * line tells them to fill it in.
 *
 * `width` is in printed points, sized for the value that goes in it.
 */
function writeIn(value: unknown, width: number): string {
  const text = String(value ?? '').trim();
  if (text) return esc(text);
  return `<span class="write-in" style="min-width:${width}pt">&nbsp;</span>`;
}

function formatDate(iso: string | null): string {
  if (!iso) return EMPTY;
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return EMPTY;
  return d.toLocaleDateString(undefined, { year: 'numeric', month: 'short', day: 'numeric' });
}

function vendorBlock(m: ManifestDetail): string {
  const parts = [
    m.vendor_name,
    m.vendor_contact_name,
    m.vendor_address_line1,
    m.vendor_address_line2,
    [m.vendor_city, m.vendor_region, m.vendor_postal_code].filter(Boolean).join(' '),
    m.vendor_country,
  ].filter((p) => p && String(p).trim());

  if (parts.length === 0) return `<div class="muted">No vendor on this manifest</div>`;
  return parts.map((p) => `<div>${esc(p)}</div>`).join('');
}

/** The order number this page may print for a line, or '' for none. */
function printablePo(line: ManifestLine): string {
  if (line.po_is_other_vendor) return '';
  return (line.po_number || '').trim();
}

/**
 * Open a print window for one manifest.
 *
 * Styled inline rather than through Tailwind: a new window shares no stylesheet
 * with the app, and this is the same approach IDCardGenerator takes for the same
 * reason. Print styles are black on white regardless of the app's theme, because
 * this is paper.
 */
export function printReturnSlip(manifest: ManifestDetail, lines: ManifestLine[]): boolean {
  const win = window.open('', '_blank');
  if (!win) {
    // The caller surfaces this. A silent no-op reads as a broken button.
    return false;
  }

  const stockLines = lines.filter(isQuantityLine);
  const unitCount = lines.filter(isPartInstanceLine).length;
  // Named for the stock case it was written for; a tracked unit retitles too,
  // because it has a catalog item and no model.
  const hasStock = stockLines.length > 0 || unitCount > 0;

  const rows = lines
    .map((line) => {
      const isDoa = line.doa_number !== null;
      const isStock = isQuantityLine(line);
      const isUnit = isPartInstanceLine(line);
      // A stock row answers the same questions with different facts: what kind
      // of thing (loose stock) and which thing (its catalog name). It has no
      // hardware UID, so the purchase order carries it, and our box barcode
      // stands in only when there is no order to print.
      // A tracked unit is one unit like a board, named like stock, and has no
      // hardware UID either, so it takes stock's box fallback.
      const kind = isStock ? 'Stock' : cell(line.effective_board_type);
      const item = isStock || isUnit
        ? cell(line.display_name || line.base_name || line.sku)
        : cell(line.effective_model_serial);
      const po = printablePo(line);
      const uid = isStock || isUnit ? (po ? '' : line.box_barcode || '') : line.primary_uid || '';
      return `
        <tr class="${isDoa ? 'doa' : ''}${isStock ? ' stock' : ''}">
          <td class="num">${line.line_number}</td>
          <td class="qty">${isStock ? (line.quantity ?? 1) : 1}</td>
          <td class="mono strong">${esc(line.label_identity)}</td>
          <td>${kind}</td>
          <td>${item}</td>
          <td class="mono small">${uid ? esc(uid) : EMPTY}</td>
          <td class="mono small nowrap">${cell(po)}</td>
          <td>${cell(line.reason_label || line.reason_raw)}</td>
          <td>${cell(line.designator)}</td>
        </tr>
      `;
    })
    .join('');

  // Named once at the top as well as per row: a desk matching this parcel to its
  // paperwork reads the header first. First-seen order, which is line order.
  const poNumbers = Array.from(new Set(lines.map(printablePo).filter(Boolean)));

  const doaCount = lines.filter((l) => l.doa_number !== null).length;
  // A board line is one unit; a stock line is its own count. Kept apart so
  // "with flashing records" cannot quietly absorb rows that never had any,
  // which includes tracked units: they never pass a flash station.
  const recordedCount = lines.length - doaCount - stockLines.length - unitCount;
  const units = stockLines.reduce((sum, l) => sum + (l.quantity || 0), 0);

  win.document.write(`
    <!DOCTYPE html>
    <html>
    <head>
      <title>Return slip ${esc(manifest.manifest_number)}</title>
      <style>
        @page { size: letter; margin: 0.5in; }
        * { box-sizing: border-box; }
        body {
          margin: 0;
          font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Helvetica, Arial, sans-serif;
          font-size: 10pt;
          color: #000;
          background: #fff;
        }
        h1 { font-size: 18pt; margin: 0 0 2pt; letter-spacing: -0.01em; }
        .manifest-number { font-family: 'Courier New', monospace; font-size: 14pt; font-weight: bold; }
        .head { display: flex; justify-content: space-between; align-items: flex-start;
                border-bottom: 2px solid #000; padding-bottom: 8pt; margin-bottom: 12pt; }
        .head-right { text-align: right; }
        /* A ruled space to write a value into. inline-block so min-width applies,
           and a bottom border rather than underscores so the rule is straight and
           its length is set in points rather than by a character count. */
        .write-in {
          display: inline-block;
          border-bottom: 0.75pt solid #000;
          vertical-align: baseline;
        }
        .blocks { display: flex; gap: 24pt; margin-bottom: 14pt; }
        .block { flex: 1; }
        .block h2 { font-size: 8pt; text-transform: uppercase; letter-spacing: 0.08em;
                    color: #555; margin: 0 0 4pt; font-weight: bold; }
        .block div { line-height: 1.4; }
        .muted { color: #777; font-style: italic; }
        table { width: 100%; border-collapse: collapse; }
        thead th {
          font-size: 7.5pt; text-transform: uppercase; letter-spacing: 0.06em;
          text-align: left; border-bottom: 1.5px solid #000; padding: 4pt 4pt 3pt;
          color: #333;
        }
        tbody td { padding: 4pt; border-bottom: 0.5pt solid #ccc; vertical-align: top; }
        /* A DOA row is a board with no record anywhere but this page. Marked so
           the vendor's desk can see the two kinds apart without reading the ID. */
        tbody tr.doa td:first-child { border-left: 3pt solid #000; }
        /* Loose stock gets its own marker rather than sharing the DOA bar: the
           two mean opposite things to a receiving desk (count these, versus
           these have no identity at all). */
        tbody tr.stock td:first-child { border-left: 3pt dotted #000; }
        .num { width: 22pt; color: #666; font-variant-numeric: tabular-nums; }
        /* Right-aligned and tabular so a column of counts can be added up by eye,
           which is what a receiving desk does with it. */
        .qty { width: 28pt; text-align: right; font-variant-numeric: tabular-nums; }
        .mono { font-family: 'Courier New', monospace; }
        .small { font-size: 8pt; }
        /* An order number breaks after every hyphen if the browser is allowed to,
           and a number split across two lines is misread. A UID never had the
           problem: it has nowhere to break. */
        .nowrap { white-space: nowrap; }
        .strong { font-weight: bold; }
        thead { display: table-header-group; }
        tr { page-break-inside: avoid; }
        .totals { margin-top: 12pt; padding-top: 8pt; border-top: 1.5px solid #000;
                  display: flex; gap: 24pt; font-size: 9pt; }
        .totals b { font-variant-numeric: tabular-nums; }
        .notes { margin-top: 12pt; font-size: 9pt; }
        .notes h2 { font-size: 8pt; text-transform: uppercase; letter-spacing: 0.08em;
                    color: #555; margin: 0 0 3pt; }
        .legend { margin-top: 14pt; font-size: 7.5pt; color: #555; line-height: 1.5; }
      </style>
    </head>
    <body>
      <div class="head">
        <div>
          <h1>Return to vendor</h1>
          <div class="manifest-number">${esc(manifest.manifest_number)}</div>
        </div>
        <div class="head-right">
          <div><strong>Vendor RMA:</strong> ${writeIn(manifest.rma_number, 120)}</div>
          <div><strong>Date:</strong> ${formatDate(manifest.sent_at || manifest.created_at)}</div>
          ${poNumbers.length ? `<div><strong>Purchase order${poNumbers.length === 1 ? '' : 's'}:</strong> <span class="mono">${poNumbers.map(esc).join(', ')}</span></div>` : ''}
        </div>
      </div>

      <div class="blocks">
        <div class="block">
          <h2>Returning to</h2>
          ${vendorBlock(manifest)}
        </div>
        <div class="block">
          <h2>Returned by</h2>
          <div>Firefly Drone Shows</div>
          <div>1900 Opdyke Ct.</div>
          <div>Auburn Hills, MI 48326</div>
        </div>
      </div>

      <table>
        <thead>
          <tr>
            <th></th>
            <th class="qty">Qty</th>
            <th>ID on label</th>
            <th>${hasStock ? 'Kind' : 'Board'}</th>
            <th>${hasStock ? 'Item' : 'Model'}</th>
            <th>Hardware UID</th>
            <th>Purchase order</th>
            <th>Reason</th>
            <th>Designator</th>
          </tr>
        </thead>
        <tbody>${rows}</tbody>
      </table>

      <div class="totals">
        <div><b>${lines.length}</b> ${hasStock ? 'line' : 'board'}${lines.length === 1 ? '' : 's'} total</div>
        <div><b>${recordedCount}</b> with flashing records</div>
        <div><b>${doaCount}</b> dead on arrival</div>
        ${unitCount ? `<div><b>${unitCount}</b> tracked unit${unitCount === 1 ? '' : 's'}</div>` : ''}
        ${stockLines.length ? `<div><b>${units}</b> unit${units === 1 ? '' : 's'} of stock</div>` : ''}
      </div>

      ${manifest.notes ? `<div class="notes"><h2>Notes</h2><div>${esc(manifest.notes)}</div></div>` : ''}

      <div class="legend">
        Each board carries a label printed with the ID in the ID column. Rows marked
        with a solid bar in the left margin are boards that could not be programmed, so no
        hardware UID was ever read from them.
        ${stockLines.length ? 'Rows marked with a dotted bar are loose stock going back by the count. They have no hardware UID and are identified by the purchase order they arrived on, or by our carton number where no order is on record.' : ''}
      </div>

      <script>
        window.onload = function () {
          window.print();
          window.onafterprint = function () { window.close(); };
        };
      </script>
    </body>
    </html>
  `);
  win.document.close();
  return true;
}
